import { afterNextRender, Component, inject, Injector, Input, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatMenu } from '@angular/material/menu';
import { MatTableDataSource } from '@angular/material/table';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { MatChipInputEvent } from '@angular/material/chips';

export const CRITERIA_LIST = [
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
  { value: 'isEqual', label: 'Is equal' },
  { value: 'isNotEqual', label: 'Is not equal' },
  { value: 'containsValue', label: 'Contains value' },
  { value: 'containsAllValues', label: 'Contains all values' },
];

export const DATE_CRITERIA_LIST = [
  { value: 'dateFrom', label: 'Date from' },
  { value: 'dateTo', label: 'Date to' },
];

export const CHECKBOX_CRITERIA_LIST = [{ value: 'containedInList', label: 'Contained in list' }];

export const TAG_CRITERIUM = { value: 'containedTagInList', label: 'Contained tag in list' };

export const MULTI_SELECT_CRITERIUM = { value: 'containedInList', label: 'Contained in list' };

export const MAX_INTEGER_FILTER_VALUE = 999_999_999;

const isNonNullObject:(item: any) => boolean = (item: any) => item != null && typeof item === 'object';

export const CRITERIA_FUNCTIONS = {
  isEmpty(value: any) {
    return value === null || value === '';
  },
  isNotEmpty(value: any) {
    return value !== null && value !== '';
  },
  isEqual(value: any, filteredValue: any) {
    if (!value || !filteredValue) {
      return;
    }
    return value.toString().toLowerCase() === filteredValue.toString().toLowerCase();
  },
  isNotEqual(value: any, filteredValue: any) {
    if (!value || !filteredValue) {
      return;
    }
    return value.toString().toLowerCase() !== filteredValue.toString().toLowerCase();
  },
  containsValue(value: any, filteredValue: any) {
    if (value == null || filteredValue == null) {
      return false;
    }
    const haystack = value.toString().toLowerCase();
    const needles = Array.isArray(filteredValue) ? filteredValue : [filteredValue];
    return needles.length > 0 && needles.some((needle: any) => haystack.includes(needle.toString().toLowerCase()));
  },
  containsAllValues(value: any, filteredValue: any) {
    if (value == null || filteredValue == null) {
      return false;
    }
    const haystack = value.toString().toLowerCase();
    const needles = Array.isArray(filteredValue) ? filteredValue : [filteredValue];
    return needles.length > 0 && needles.every((needle: any) => haystack.includes(needle.toString().toLowerCase()));
  },
  dateFrom(value: any, selectedValue: any) {
    return Date.parse(selectedValue) <= Date.parse(value);
  },
  dateTo(value: any, selectedValue: any) {
    return Date.parse(selectedValue) >= Date.parse(value);
  },
  containedInList(value: any, filteredValueList: any) {
    if (!filteredValueList) {
      return;
    }
    let contained = false;
    for (const listItem of filteredValueList) {
      const listValue = isNonNullObject(listItem) ? listItem.name : listItem;
      if (listValue === value) {
        contained = true;
      }
    }
    return contained;
  },
  containedTagInList(value: any, filteredValueList: any) {
    if (!filteredValueList) {
      return;
    }
    let contained = false;
    for (const listItem of filteredValueList) {
      const listValue = isNonNullObject(listItem) ? listItem.name : listItem;
      if (value.includes(listValue)) {
        contained = true;
      }
    }
    return contained;
  },
};

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

const MULTI_SELECT_FILTER_WIDTH = 300;

enum FilterMenuType {
  TEXT = 'text',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  TAG = 'tag',
  MULTI_SELECT = 'multiSelect',
}

@Component({
  selector: 'app-filter-menu',
  templateUrl: './filter-menu.component.html',
  styleUrls: ['./filter-menu.component.scss'],
  exportAs: 'columnFilterComponent',
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class FilterMenuComponent {
  @ViewChild('filterMenu', { static: true }) filterMenu!: MatMenu;
  @ViewChild('dateFilterMenu', { static: true }) dateFilterMenu!: MatMenu;
  @ViewChild('checkboxFilterMenu', { static: true }) checkboxFilterMenu!: MatMenu;
  @ViewChild('tagFilterMenu', { static: true }) tagFilterMenu!: MatMenu;
  @ViewChild('multiSelectFilterMenu', { static: true }) multiSelectFilterMenu!: MatMenu;

  @Input() dataSource!: MatTableDataSource<any> | PaginationDataSource<any>;

  public criteriaList = CRITERIA_LIST;
  public dateCriteriaList = DATE_CRITERIA_LIST;
  public checkboxCriteriaList = CHECKBOX_CRITERIA_LIST;
  public tagCriterium = TAG_CRITERIUM;
  public multiSelectCriterium = MULTI_SELECT_CRITERIUM;
  private filterMethods = CRITERIA_FUNCTIONS;
  public searchValue: any = {};
  public searchCriterion: any = {};
  public filterMenuType = FilterMenuType;

  selectedCategories: any = {};
  selectedTags: any = {};

  formControlsMap: Record<string, FormControl<string[] | null>> = {};
  selectAllValue = 'SELECT_ALL';
  multiSelectFilterWidth = MULTI_SELECT_FILTER_WIDTH;
  compareWith: (item1: string, item2: string) => boolean = (item1: string, item2: string) => item1 === item2;

  public filterMode: Record<string, 'AND' | 'OR'> = {};
  public textFilterValues: Partial<Record<string, string[]>> = {};
  public showChipInput: Record<string, boolean> = {};
  public searchTerm: Record<string, string> = {};

  private readonly injector = inject(Injector);

  getFormControl(caller: string): FormControl<string[] | null> {
    if (this.formControlsMap[caller] != null) {
      return this.formControlsMap[caller];
    }
    this.formControlsMap[caller] = new FormControl<string[]>([]);
    return this.formControlsMap[caller];
  }

  toggleFilterMode(caller: string): void {
    this.filterMode[caller] = this.filterMode[caller] === 'AND' ? 'OR' : 'AND';
  }

  applyColumnFilter(caller?: string) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(caller, this.getTextFilterValues(caller), mode);
      }
      return;
    }

    if (caller) {
      const values = this.getTextFilterValues(caller);
      if (values.length > 0) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.searchCriterion[caller] = mode === 'AND' ? 'containsAllValues' : this.criteriaList[4].value;
        this.searchValue[caller] = values;
      } else {
        delete this.searchCriterion[caller];
        delete this.searchValue[caller];
      }
    }
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  applyDateColumnFilter(caller: string) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller && this.searchValue[caller]) {
        this.dataSource.addFilterOptions(caller, [this.searchValue[caller]]);
      }
      return;
    }

    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };

    this.dataSource.filter = searchFilter;
  }

  applyTagColumnFilter(caller: string, targetList: { name: string; value: string }[]) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(caller, (targetList ?? []).map(x => x.value), mode);
      }

      return;
    }

    if (targetList && targetList.length > 0) {
      this.searchCriterion[caller] = this.tagCriterium.value;
      this.searchValue[caller] = targetList;
    } else {
      delete this.searchCriterion[caller];
      delete this.searchValue[caller];
    }
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  applyMultiSelectColumnFilter(caller: string) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller && this.searchValue[caller]) {
        const mode = this.filterMode[caller] ?? 'OR';
        const values = (this.searchValue[caller] as string[]).filter(_ => this.isNotSelectAllValue(_));
        this.dataSource.addFilterOptions(caller, values, mode);
      }

      return;
    }

    const values = this.formControlsMap[caller]?.value?.filter(_ => this.isNotSelectAllValue(_)) ?? [];
    if (values.length > 0) {
      this.searchCriterion[caller] = this.multiSelectCriterium.value;
      this.searchValue[caller] = values;
    } else {
      delete this.searchCriterion[caller];
      delete this.searchValue[caller];
    }
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  applyCheckboxColumnFilter(caller: string, targetList: { name: string; value: string }[]) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (caller) {
        const mode = this.filterMode[caller] ?? 'OR';
        this.dataSource.addFilterOptions(caller, (targetList ?? []).map(x => x.value), mode);
      }

      return;
    }

    if (targetList && targetList.length > 0) {
      this.searchCriterion[caller] = this.checkboxCriteriaList[0].value;
      this.searchValue[caller] = targetList;
    } else {
      delete this.searchCriterion[caller];
      delete this.searchValue[caller];
    }
    const searchFilter: any = {
      value: this.searchValue,
      criterion: this.searchCriterion,
      allCriteria: this.filterMethods,
    };
    this.dataSource.filter = searchFilter;
  }

  clearAll(): void {
    this.searchValue = {};
    this.searchCriterion = {};
    this.selectedCategories = {};
    this.selectedTags = {};
    this.filterMode = {};
    this.textFilterValues = {};
    this.showChipInput = {};
    this.searchTerm = {};
    Object.values(this.formControlsMap).forEach(control => control.setValue([]));
  }

  clearColumn(columnKey: string): void {
    delete this.searchValue[columnKey];
    delete this.searchCriterion[columnKey];
    this.selectedCategories[columnKey] = undefined;
    this.selectedTags[columnKey] = undefined;
    this.filterMode[columnKey] = 'OR';
    this.textFilterValues[columnKey] = [];
    this.searchTerm[columnKey] = '';
    this.formControlsMap[columnKey]?.setValue([]);

    if (this.dataSource instanceof PaginationDataSource) {
      this.applyColumnFilter(columnKey);
    } else {
      this.applyColumnFilter();
    }
  }

  setSelected(checked: boolean, category: { name: string; value: any }, targetList: any, caller: string) {
    if (checked) {
      if (!targetList[caller]) {
        targetList[caller] = [];
      }
      targetList[caller].push(category);
    } else {
      const index = targetList[caller].findIndex((x: { value: any }) => x.value === category.value);
      targetList[caller].splice(index, 1);
      if (targetList[caller].length === 0) {
        targetList[caller] = undefined;
      }
    }
  }

  isSelected(category: { name: string; value: any }, targetList: { name: string; value: any }[]) {
    if (targetList && targetList.length > 0) {
      return targetList.some(x => x.value === category.value);
    }
  }

  multiSelectValueChanged(value: string | string[], caller: string) {
    this.searchValue[caller] = value;
  }

  preventEventBubbling(event: Event): void {
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  // TODO: after angular update to new version, consider using afterRenderEffect
  focusAfterRender(element: HTMLInputElement): void {
    afterNextRender(() => element.focus(), { injector: this.injector });
  }

  private isNotSelectAllValue(item: string): boolean {
    return !this.compareWith(item, this.selectAllValue);
  }

  private isValidIntegerFilterValue(value: string): boolean {
    return /^\d+$/.test(value) && Number(value) <= MAX_INTEGER_FILTER_VALUE;
  }

  blockNonDigits(event: InputEvent, integerOnly: boolean): void {
    if (!integerOnly) return;
    const inserted = event.data ?? event.dataTransfer?.getData('text') ?? '';
    if (/\D/.test(inserted)) {
      event.preventDefault();
    }
  }

  addChipValue(caller: string, event: MatChipInputEvent, integerOnly = false): void {
    const value = event.value.trim();
    if (value && (!integerOnly || this.isValidIntegerFilterValue(value))) {
      this.getTextFilterValues(caller).push(value);
    }
    event.chipInput?.clear();
  }

  addChipValueAndHideInput(caller: string, event: MatChipInputEvent, integerOnly = false): void {
    this.addChipValue(caller, event, integerOnly);
    this.showChipInput[caller] = false;
  }

  getTextFilterValues(caller: string): string[] {
    if (!this.textFilterValues[caller]) {
      this.textFilterValues[caller] = [];
    }
    return this.textFilterValues[caller];
  }

  removeTextFilterValue(caller: string, index: number): void {
    this.textFilterValues[caller]?.splice(index, 1);
  }

  getFilteredOptions(caller: string, options: { name: string; value: any }[]): { name: string; value: any }[] {
    const term = this.searchTerm[caller]?.toLowerCase().trim();
    if (!term) {
      return options;
    }
    return options.filter(o => o.name.toLowerCase().includes(term));
  }

  isAllOptionsSelected(caller: string, options: { name: string; value: any }[], targetList: Record<string, { name: string; value: any }[]>): boolean {
    const selected = targetList[caller]?.length ?? 0;
    return selected > 0 && selected === options.length;
  }

  isOptionsIndeterminate(caller: string, options: { name: string; value: any }[], targetList: Record<string, { name: string; value: any }[]>): boolean {
    const selected = targetList[caller]?.length ?? 0;
    return selected > 0 && selected < options.length;
  }

  toggleSelectAllOptions(checked: boolean, options: { name: string; value: any }[], targetList: any, caller: string): void {
    targetList[caller] = checked ? [...options] : undefined;
  }

  handleFilterAction(menuType: FilterMenuType, caller: string, isClear = false): void {
    if (isClear) {
      this.clearColumn(caller);
    } else {
      switch (menuType) {
        case FilterMenuType.TEXT:
          this.applyColumnFilter(caller);
          break;
        case FilterMenuType.DATE:
          this.applyDateColumnFilter(caller);
          break;
        case FilterMenuType.CHECKBOX:
          this.applyCheckboxColumnFilter(caller, this.selectedCategories[caller]);
          break;
        case FilterMenuType.TAG:
          this.applyTagColumnFilter(caller, this.selectedTags[caller]);
          break;
        case FilterMenuType.MULTI_SELECT:
          this.applyMultiSelectColumnFilter(caller);
          break;
      }
    }
    this.closeMenuFor(menuType);
  }

  private closeMenuFor(menuType: FilterMenuType): void {
    const menus: Record<FilterMenuType, MatMenu> = {
      [FilterMenuType.TEXT]: this.filterMenu,
      [FilterMenuType.DATE]: this.dateFilterMenu,
      [FilterMenuType.CHECKBOX]: this.checkboxFilterMenu,
      [FilterMenuType.TAG]: this.tagFilterMenu,
      [FilterMenuType.MULTI_SELECT]: this.multiSelectFilterMenu,
    };
    menus[menuType].closed.emit('click');
  }
}
